import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useContext } from 'react';

import {
    actionsCellSx,
    bankAccountSelectSx,
    deleteRowButtonSx,
    iconTrashSx,
    paymentTableCardSx,
    paymentTableSx,
} from '../../pages/dashboard/DashboardStyles';
import { formatCurrency } from '../../utils/helper';
import { useTranslation } from '../../utils/translations';
import CommonDatePicker, { DatePickerValidationReason } from '../date-picker/CommonDatePicker';
import { VerificationBannerContext } from '../layout/dashboard-layout';
import { ScrollableTableWrapper } from '../scrollable-table-wrapper';
import { CustomSelect } from '../select/CustomSelect';
import CommonTable from '../tableComponent/commonTableComponent';
import { colors } from '../theme/theme';

import { PaymentAmountCell } from './PaymentAmountCell';
import { PaymentRowNotices } from './PaymentRowNotices';
import { RowAutoPayAction } from './RowAutoPayAction';

import type { CardFeeByRowEntry } from './PaymentRowNotices';
import type { BankAccountOption } from '../../hooks/make-payment/useContractPaymentData';
import type { AmountChoice, PaymentMethodType, PaymentRow } from '../../hooks/make-payment/usePaymentRows';
import type { ContractApiResponse } from '../../services/contract-services';
import type { OpenInvoiceApiResponse } from '../../services/payment-services';
import type { SavedPaymentMethodDisplay } from '../modals/AddPaymentMethodModal';
import type { Dispatch, SetStateAction } from 'react';

interface PaymentRowsTableProps {
    paymentRows: PaymentRow[];
    paymentMethodType: PaymentMethodType;
    cardFeeByRow: CardFeeByRowEntry[];
    cardIneligibleContracts: ContractApiResponse[];
    savedPaymentMethods: SavedPaymentMethodDisplay[];
    bankAccountOptions: BankAccountOption[];
    paymentRowErrors: Array<{ bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean }>;
    /** Per-row backend validation messages from the last submit attempt (e.g. duplicate payment, amount exceeds invoice). */
    paymentSubmitErrors?: Array<string | null>;
    paymentDatePickerErrors: string[];
    cardFeesLoading?: boolean;
    openInvoices: OpenInvoiceApiResponse[];
    resolveContractForPaymentRow: (row: PaymentRow) => ContractApiResponse | null;
    getContractById: (contractId: string) => ContractApiResponse | null;
    isConsumerLoanContract: (contract: ContractApiResponse | null | undefined) => boolean;
    handlePaymentCardChange: (index: number, value: string) => void;
    handleBankAccountChange: (index: number, value: string) => void;
    handleDateChange: (index: number, value: dayjs.Dayjs | null) => void;
    handleDatePickerError: (index: number, reason: DatePickerValidationReason, meta?: { rawInput: string }) => void;
    handleDeleteRow: (index: number) => void;
    handleAmountChoiceChange: (index: number, choice: AmountChoice) => void;
    getRowCardFeeAmount: (row: PaymentRow, rowIndex: number) => number;
    setOtherAmountInputRef: (index: number, el: HTMLInputElement | null) => void;
    setPaymentRows: Dispatch<SetStateAction<PaymentRow[]>>;
    setPaymentRowErrors: Dispatch<
        SetStateAction<Array<{ bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean }>>
    >;
    setPaymentSubmitErrors: Dispatch<SetStateAction<Array<string | null>>>;
    /** Whether Stripe is enabled (1 = enabled, 0 = disabled) */
    isStripeEnabled?: boolean;
}

/**
 * The desktop payment-rows CommonTable: contract, bank account/card, payment date, due amount, and
 * actions columns. Hidden below the `md` breakpoint — mobile renders each contract as an accordion
 * card via `ContractSidebar` + `MobilePaymentContractCard` instead.
 */
export const PaymentRowsTable: React.FC<PaymentRowsTableProps> = ({
    paymentRows,
    paymentMethodType,
    cardFeeByRow,
    cardIneligibleContracts,
    savedPaymentMethods,
    bankAccountOptions,
    paymentRowErrors,
    paymentSubmitErrors,
    paymentDatePickerErrors,
    cardFeesLoading,
    openInvoices,
    resolveContractForPaymentRow,
    isConsumerLoanContract,
    handlePaymentCardChange,
    handleBankAccountChange,
    handleDateChange,
    handleDatePickerError,
    handleDeleteRow,
    handleAmountChoiceChange,
    getRowCardFeeAmount,
    setOtherAmountInputRef,
    setPaymentRows,
    setPaymentRowErrors,
    setPaymentSubmitErrors,
    isStripeEnabled = false,
}) => {
    const { t } = useTranslation();
    const showVerificationBanner = useContext(VerificationBannerContext);

    const hasContractInvoices = (contractId: string): boolean => {
        const searchId = String(contractId).trim().toUpperCase();
        return openInvoices.some((inv: OpenInvoiceApiResponse) => {
            const aspireId = String(inv?.AspireContractId || inv?.aspireContractId || '')
                .trim()
                .toUpperCase();
            const dbId = String(inv?.contractId || inv?.ContractId || '')
                .trim()
                .toUpperCase();
            return aspireId === searchId || dbId === searchId;
        });
    };

    return (
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <ScrollableTableWrapper scrollAmount={200} showIndicators={true}>
                <CommonTable
                    className="tableWrapperLG"
                    columns={[
                        { id: 'contractId', label: t('payments.createPayment.contractId') },
                        {
                            id: 'bankAccount',
                            label:
                                isStripeEnabled && paymentMethodType === 'card'
                                    ? t('makePayment.paymentCard')
                                    : t('payments.createPayment.paymentAccount'),
                        },
                        // Card payments always use the current date, so the Payment Date
                        // column is hidden for card and only shown for bank account.
                        ...(isStripeEnabled && paymentMethodType === 'card'
                            ? []
                            : [
                                  {
                                      id: 'paymentDate',
                                      label: t('payments.createPayment.paymentDate'),
                                  },
                              ]),
                        { id: 'dueAmount', label: t('payments.createPayment.dueAmount') },
                        { id: 'scheduled', label: t('payments.paymentMethod.scheduled') },
                        { id: 'autopay', label: t('payments.createPayment.autopay') },
                        { id: 'actions', label: 'Action', align: 'center' as const },
                    ]}
                    getRowSx={(_row, rowIndex) => {
                        const row = paymentRows[rowIndex];
                        const contract = resolveContractForPaymentRow(row);
                        const outstandingBalance =
                            contract?.OutstandingBalance != null
                                ? Number(contract.OutstandingBalance)
                                : contract?.RemainingPrincipal != null
                                  ? Number(contract.RemainingPrincipal)
                                  : null;

                        const scheduledAmountNum = parseFloat(String(row.scheduledAmount ?? '0')) || 0;
                        const isBalanceFullyCovered =
                            outstandingBalance != null &&
                            scheduledAmountNum >= outstandingBalance &&
                            outstandingBalance > 0;

                        if (rowIndex === paymentRows.length && cardIneligibleContracts.length > 0) {
                            return {
                                pointerEvents: 'none',
                                bgcolor: colors.warningPreset[90],
                                borderTop: `2px solid ${colors.warningPreset[80]}`,
                                borderBottom: `1px solid ${colors.warningPreset[80]}`,
                                '& .MuiTableCell-root:first-of-type': {
                                    overflow: 'visible',
                                    position: 'relative',
                                    zIndex: 1,
                                },
                            };
                        }
                        if (rowIndex > paymentRows.length) {
                            return {
                                pointerEvents: 'none',
                                bgcolor: colors.greyPreset[5],
                                borderLeft: `3px solid ${colors.greyPreset[20]}`,
                                '& .MuiTableCell-root': { py: 0.75 },
                            };
                        }
                        if (isBalanceFullyCovered) {
                            return { opacity: 0.5, pointerEvents: 'none', position: 'relative' };
                        }
                        if (cardFeeByRow[rowIndex]?.isDisabled || cardFeeByRow[rowIndex]?.isLoading) {
                            return { opacity: 0.5, pointerEvents: 'none', position: 'relative' };
                        }
                        return {};
                    }}
                    rows={paymentRows.map((row, index) => {
                        const contract = resolveContractForPaymentRow(row);
                        const outstandingBalance =
                            contract?.OutstandingBalance != null
                                ? Number(contract.OutstandingBalance)
                                : contract?.RemainingPrincipal != null
                                  ? Number(contract.RemainingPrincipal)
                                  : null;

                        const savedCardOptions = savedPaymentMethods
                            .filter(pm => {
                                if (pm.type !== 'card') return false;
                                if (!pm.expiryMonth || !pm.expiryYear) return true;
                                const expiryDate = new Date(
                                    Number(pm.expiryYear),
                                    Number(pm.expiryMonth),
                                    0,
                                    23,
                                    59,
                                    59,
                                    999,
                                );
                                return expiryDate >= new Date();
                            })
                            .map(pm => ({ id: pm.id, name: pm.label }));

                        const selectedCard = savedPaymentMethods.find(pm => pm.id === row.paymentCardId);
                        return {
                            contractId: (
                                <Stack position="relative">
                                    <Typography variant="body2">{row.contractId}</Typography>
                                    {paymentMethodType === 'card' && cardFeeByRow[index]?.isLoading && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                zIndex: 10,
                                            }}
                                        >
                                            <CircularProgress size={20} thickness={4} />
                                        </Box>
                                    )}
                                </Stack>
                            ),
                            bankAccount:
                                paymentMethodType === 'card' ? (
                                    <CustomSelect
                                        id={`payment-card-select-${index}`}
                                        ariaLabel={t('makePayment.paymentCard')}
                                        labelSrOnly
                                        value={row.paymentCardId}
                                        onChange={value => handlePaymentCardChange(index, value)}
                                        options={savedCardOptions}
                                        placeholder={
                                            savedCardOptions.length === 0
                                                ? t('makePayment.noSavedCardsClickAdd')
                                                : t('makePayment.selectPaymentCard')
                                        }
                                        noOptionsMessage={t('makePayment.noSavedCards')}
                                        sx={bankAccountSelectSx}
                                        error={!!paymentRowErrors[index]?.bankAccount}
                                    />
                                ) : (
                                    <CustomSelect
                                        id={`bank-account-select-${index}`}
                                        ariaLabel={t('payments.createPayment.paymentAccount')}
                                        labelSrOnly
                                        value={row.bankAccount}
                                        onChange={value => handleBankAccountChange(index, value)}
                                        options={bankAccountOptions}
                                        placeholder={
                                            bankAccountOptions.length === 0
                                                ? t('payments.createPayment.noBankAccountsPresent')
                                                : t('payments.createPayment.selectBankAccount')
                                        }
                                        noOptionsMessage={t('payments.createPayment.noBankAccountsPresent')}
                                        sx={bankAccountSelectSx}
                                        error={!!paymentRowErrors[index]?.bankAccount}
                                    />
                                ),
                            paymentDate: (
                                <Tooltip title={''} placement="top" arrow>
                                    <span>
                                        <CommonDatePicker
                                            value={row.paymentDate}
                                            onChange={value => handleDateChange(index, value)}
                                            onPickerError={(reason, _v, meta) =>
                                                handleDatePickerError(index, reason, meta)
                                            }
                                            DatePickerErrorValue={paymentDatePickerErrors[index]}
                                            calenderIconColor={colors.greyPreset[80]}
                                            minDate={dayjs().startOf('day')}
                                            maxDate={
                                                paymentMethodType === 'card'
                                                    ? dayjs().startOf('day')
                                                    : (contract?.MaturityDate ? dayjs(contract.MaturityDate) : dayjs())
                                                          .add(90, 'day')
                                                          .startOf('day')
                                            }
                                            ariaLabel={`Payment date row ${index + 1}`}
                                            error={!!paymentRowErrors[index]?.paymentDate}
                                        />
                                    </span>
                                </Tooltip>
                            ),
                            dueAmount: (
                                <PaymentAmountCell
                                    row={row}
                                    rowIndex={index}
                                    paymentMethodType={paymentMethodType}
                                    isConsumerLoan={isConsumerLoanContract(contract)}
                                    outstandingBalance={outstandingBalance}
                                    hasContractInvoices={hasContractInvoices(row.contractId)}
                                    getRowCardFeeAmount={getRowCardFeeAmount}
                                    paymentAmountError={paymentRowErrors[index]?.paymentAmount}
                                    cardFeesLoading={cardFeesLoading}
                                    selectedCardFunding={selectedCard?.funding}
                                    selectedCardCountry={selectedCard?.country}
                                    setPaymentRows={setPaymentRows}
                                    setPaymentRowErrors={setPaymentRowErrors}
                                    setPaymentSubmitErrors={setPaymentSubmitErrors}
                                    handleAmountChoiceChange={handleAmountChoiceChange}
                                    setOtherAmountInputRef={setOtherAmountInputRef}
                                />
                            ),
                            scheduled: (() => {
                                const scheduledAmountStr = String(row.scheduledAmount ?? '0');
                                const scheduledAmountNum = parseFloat(scheduledAmountStr) || 0;
                                const amountStr = formatCurrency(scheduledAmountNum);
                                return scheduledAmountNum > 0 ? (
                                    <Tooltip title={amountStr} placement="top" arrow>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent={'center'}
                                            sx={{
                                                p: '12px 8px',
                                                borderRadius: 3,
                                                color: colors.warningPreset[50],
                                                bgcolor: colors.warningPreset[60],
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Typography variant="body2" noWrap>
                                                {amountStr}
                                            </Typography>
                                        </Stack>
                                    </Tooltip>
                                ) : (
                                    <Typography variant="body2">{amountStr}</Typography>
                                );
                            })(),
                            autopay: <RowAutoPayAction contract={contract} />,
                            actions: (
                                <Box sx={{ ...actionsCellSx, position: 'relative', zIndex: 2 }}>
                                    <IconButton
                                        onClick={() => handleDeleteRow(index)}
                                        size="small"
                                        sx={deleteRowButtonSx}
                                        aria-label={`Delete row ${index + 1}`}
                                    >
                                        <span className="icon-trash" style={iconTrashSx as React.CSSProperties} />
                                    </IconButton>
                                </Box>
                            ),
                        };
                    })}
                    stickyHeader={true}
                    hideActionsShadow
                    maxHeight={showVerificationBanner ? 'calc(100dvh - 350px)' : 'calc(100dvh - 314px)'}
                    sx={paymentMethodType === 'card' ? paymentTableCardSx : paymentTableSx}
                    renderRowDetail={(_, rowIndex) => {
                        const row = paymentRows[rowIndex];
                        if (!row) return null;
                        return (
                            <PaymentRowNotices
                                row={row}
                                contract={resolveContractForPaymentRow(row)}
                                paymentMethodType={paymentMethodType}
                                cardFeeEntry={cardFeeByRow[rowIndex]}
                                submitError={paymentSubmitErrors?.[rowIndex]}
                            />
                        );
                    }}
                />
            </ScrollableTableWrapper>
        </Box>
    );
};
